import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider } from "@mui/material/styles";
import ResponsiveAppBar from "../components/Navbar/index2";
import Footer from "../components/Footer/footer";
import { Constants } from "../config/constants";
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import SupportButton from '../components/Buttons/supportButton';

const Home = lazy(() => import("../pages/home"));
const GoodsList = lazy(() => import("../pages/goodsList"));
const GoodsItem = lazy(() => import("../pages/goodsItem"));
const Calculators = lazy(() => import("../pages/calculators"));
const BarnSizeCalculator = lazy(() => import("../pages/barnSizeCalculator"));
const SiloSizeCalculator = lazy(() => import("../pages/siloSizeCalculator"));
const TackleBoxSizeCalculator = lazy(() => import("../pages/tackleBoxSizeCalculator"));
const BemsCalculator = lazy(() => import("../pages/bemsCalculator"));
const DecoCostCalculator = lazy(() => import("../pages/decoCostCalculator"));
const FoodListMaker = lazy(() => import("../pages/foodListMaker"));
const ContactUs = lazy(() => import("../pages/contactUs"));
const PriceCalculator = lazy(() => import("../pages/priceCalculator"));
const TownToolsCalculator = lazy(() => import("../pages/townToolsCalculator"));
const ProductionBuildingsList = lazy(() => import("../pages/productionBuildingsList"));
const ProductionBuildingDetails = lazy(() => import("../pages/productionBuildingDetails"));
const DownloadHayDay = lazy(() => import("../pages/downloadHayDay"));
const TreesAndBushesList = lazy(() => import("../pages/treesAndBushesList"));
const TreesAndBushesProfitCalculator = lazy(() => import("../pages/treesAndBushesProfitCalculator"));
const TreesAndBushesDetails = lazy(() => import("../pages/treesAndBushesDetails"));
const BuildingsSlotsCalculator = lazy(() => import("../pages/buildingsSlotsCalculator"));
const BuildingsCoinsCalculator = lazy(() => import("../pages/buildingsCoinsCalculator"));
const MiningOresCalculator = lazy(() => import("../pages/miningOresCalculator"));
const MiningDiamondsCalculator = lazy(() => import("../pages/miningDiamondsCalculator"));
const XPLevelCalculator = lazy(() => import("../pages/xpLevelCalculator"));
const GoodsBreakdown = lazy(() => import("../pages/goodsBreakdown"));
const AddFriendsPage = lazy(() => import("../pages/addFriends/addFriendsPage"));
const DonatePage = lazy(() => import("../pages/donatePage"));
const ExpansionPermitDetails = lazy(() => import("../pages/supplies/expansionPermit"));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress sx={{ color: '#8b5a2b' }} />
  </Box>
);

const rootTheme = createTheme({
  palette: {
    background: {
      default: 'transparent'
    }
  }
});

function App() {
  const [data, setData] = useState([]);
  const [images, setImages] = useState([]);

  useEffect(() => {
    let oldCache = Constants.old_cache;
    for (let cache of oldCache) {
      localStorage.removeItem(cache);
    }

    if (Constants.takeDataFromFirebase) {
      let cacheData = localStorage.getItem(Constants.cached_goods);
      if (cacheData) {
        setData(JSON.parse(cacheData));
      } else {
        getData();
      }
    }
  }, []);

  const getData = () => {
    console.log("Firebase API called on main app");
    import("../api/fetch")
      .then(({ getGoods }) => getGoods())
      .then((res) => {
        setData(res);
        localStorage.setItem(Constants.cached_goods, JSON.stringify(res));
      })
      .catch((err) => console.error("Failed to load goods from Firebase", err));
  };

  return (
    <StyledEngineProvider injectFirst>
    <ThemeProvider theme={rootTheme}>
    <CssBaseline />
    <div className="App"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundImage: `url(https://wallpaperaccess.com/full/3022015.jpg)`,
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        backgroundPosition: "center",
        backgroundSize: "cover"
      }}
    >
      <Router>
        <ResponsiveAppBar />

        <div style={{ flexGrow: 1, paddingBottom: "100px" }}>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" exact element={<Home goods={data} />} />
            <Route path="/home" exact element={<Home goods={data} />} />
            <Route path="/calculators" exact element={<Calculators />} />
            <Route path="/goodsList" exact element={<GoodsList goods={data} images={images} />} />
            <Route path="/barnSizeCalculator" exact element={<BarnSizeCalculator />} />
            <Route path="/siloSizeCalculator" exact element={<SiloSizeCalculator />} />
            <Route path="/tackleBoxSizeCalculator" exact element={<TackleBoxSizeCalculator />} />
            <Route path="/bemsCalculator" element={<BemsCalculator />} />
            <Route path="/decoCostCalculator" element={<DecoCostCalculator />} />
            <Route path="/foodListMaker" element={<FoodListMaker goods={data} />} />
            <Route path="/contactUs" element={<ContactUs />} />
            <Route path="/productionBuildingsList" element={<ProductionBuildingsList />} />
            <Route path="/treesAndBushesList" element={<TreesAndBushesList />} />
            <Route path="/buildingsSlotsCalculator" element={<BuildingsSlotsCalculator />} />
            <Route path="/treesAndBushesList/:treeName" element={<TreesAndBushesDetails />} />
            <Route path="/treesAndBushesProfitCalculator" element={<TreesAndBushesProfitCalculator />} />
            <Route path="/priceCalculator" element={<PriceCalculator goods={data} />} />
            <Route path="/townToolsCalculator" element={<TownToolsCalculator images={images} />} />
            <Route path="/goodsList/:goodsName" element={<GoodsItem />} />
            <Route path="/supplies/expansion-permit" element={<ExpansionPermitDetails />} />
            <Route path="/download/hay-day-apk" element={<DownloadHayDay />} />
            <Route path="/productionBuildingsList/:productionBuildingName" element={<ProductionBuildingDetails />} />
            <Route path="/miningOresCalculator" element={<MiningOresCalculator />} />
            <Route path="/miningDiamondsCalculator" element={<MiningDiamondsCalculator />} />
            <Route path="/buildingsCoinsCalculator" element={<BuildingsCoinsCalculator />} />
            <Route path="/xpLevelCalculator" element={<XPLevelCalculator />} />
            <Route path="/goodsBreakdown" element={<GoodsBreakdown />} />
            <Route path="/addFriendsPage" element={<AddFriendsPage />} />
            <Route path="/donate" element={<DonatePage />} />
          </Routes>
          </Suspense>
        </div>
        <Box
  sx={{
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 1000
  }}
>
  <SupportButton
    variant="icon-only"
    animate={true}
    tooltip="Like this tool? Consider supporting us!"
  />
</Box>
        <Footer />
      </Router>
    </div>
    </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;

